import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { useUserRealtime } from "../../context/UserRealtimeContext";
import { SettingsIcon } from "../../widgets/icons";
import { isAccountVerified } from "../utils";

const iconClass = "h-[17px] w-[17px] shrink-0";

function HomeIcon({ className }) {
  return (
    <svg className={cn(iconClass, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}

function SearchIcon({ className }) {
  return (
    <svg className={cn(iconClass, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4 4" />
    </svg>
  );
}

function UsersIcon({ className }) {
  return (
    <svg className={cn(iconClass, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="9" cy="9" r="3.2" />
      <path d="M3.5 19c.6-3 3-4.5 5.5-4.5s4.9 1.5 5.5 4.5" />
      <circle cx="17" cy="8" r="2.5" />
      <path d="M15.5 13.7c.5-.2 1-.3 1.5-.3 2 0 3.6 1.2 4 3.1" />
    </svg>
  );
}

function MessageIcon({ className }) {
  return (
    <svg className={cn(iconClass, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 5h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H8l-4 4V7a2 2 0 0 1 2-2z" />
    </svg>
  );
}

function PlusIcon({ className }) {
  return (
    <svg className={cn(iconClass, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function UserIcon({ className }) {
  return (
    <svg className={cn(iconClass, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5 19c1-3 3.5-4.5 7-4.5s6 1.5 7 4.5" />
    </svg>
  );
}

function BuildingIcon({ className }) {
  return (
    <svg className={cn(iconClass, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 20V6l8-3 8 3v14" />
      <path d="M9 20v-5h6v5" />
      <path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01" />
    </svg>
  );
}

function MenuIcon({ className }) {
  return (
    <svg className={cn(iconClass, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function ClipboardIcon({ className }) {
  return (
    <svg className={cn(iconClass, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 5h6a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" />
      <path d="M9 5V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1" />
      <path d="M9 12h6M9 16h4" />
    </svg>
  );
}

function CloseIcon({ className }) {
  return (
    <svg className={cn(iconClass, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/**
 * Build sidebar sections for the logged-in shell.
 * @param {{ id?: number, type?: string, affiliations?: Array<object> }} user
 * @param {{
 *   pendingConnectionCount?: number,
 *   pendingAffiliationCount?: number,
 *   messagingUnreadCount?: number,
 *   orgUnreadById?: Record<string, number>,
 * }} [badges]
 * @returns {{ group: string, items: { path: string, label: string, Icon: Function, badgeCount?: number, badgeAria?: string }[] }[]}
 */
function buildNavSections(user, badges = {}) {
  const isOrg = user?.type === "organization";
  const verified = isAccountVerified(user);
  const profilePath = isOrg
    ? `/organization/${user.id}`
    : user?.id
      ? `/profile/${user.id}`
      : "/login";

  const pendingConnectionCount = Number(badges.pendingConnectionCount) || 0;
  const pendingAffiliationCount = Number(badges.pendingAffiliationCount) || 0;
  const networkBadgeCount = pendingConnectionCount + pendingAffiliationCount;
  const messagingUnreadCount = Number(badges.messagingUnreadCount) || 0;
  const orgUnreadById = badges.orgUnreadById || {};

  const mainItems = [];
  if (verified) {
    mainItems.push({ path: "/feed", label: "Home", Icon: HomeIcon });
  }
  mainItems.push({ path: "/explore", label: "Explore", Icon: SearchIcon });
  if (!isOrg && verified) {
    mainItems.push(
      {
        path: "/network",
        label: "Network",
        Icon: UsersIcon,
        badgeCount: networkBadgeCount,
        badgeAria: "pending network requests",
      },
      {
        path: "/messaging",
        label: "Messages",
        Icon: MessageIcon,
        badgeCount: messagingUnreadCount,
        badgeAria: "unread",
      }
    );
  }

  const secondaryItems = isOrg
    ? [
        ...(verified
          ? [{ path: "/create-post", label: "Create", Icon: PlusIcon }]
          : []),
        { path: profilePath, label: "Profile", Icon: UserIcon },
        { path: "/organization-admin", label: "Manage", Icon: BuildingIcon },
        { path: "/settings", label: "Settings", Icon: SettingsIcon },
      ]
    : [
        ...(verified
          ? [{ path: "/create-post", label: "Create", Icon: PlusIcon }]
          : []),
        { path: profilePath, label: "My Profile", Icon: UserIcon },
        { path: "/settings", label: "Settings", Icon: SettingsIcon },
      ];

  const sections = [
    { group: "Main", items: mainItems },
    { group: isOrg ? "Organization" : "You", items: secondaryItems },
  ];

  if (!isOrg) {
    const orgItems = (user?.affiliations || [])
      .filter((a) => a.status === "active" && a.organization)
      .map((a) => {
        const orgId = Number(a.organization.id);
        return {
          path: `/organization/${orgId}/channels`,
          label: a.organization.name || "Organization",
          Icon: BuildingIcon,
          badgeCount: Number(orgUnreadById[String(orgId)]) || 0,
          badgeAria: "unread",
        };
      });
    if (orgItems.length > 0) {
      sections.push({ group: "Organizations", items: orgItems });
    }
  }

  if (!isOrg) {
    sections.push({
      group: "Beta",
      items: [{ path: "/beta", label: "Feedback", Icon: ClipboardIcon }],
    });
  }

  return sections;
}

/**
 * @param {string} path
 * @param {string} pathname
 * @returns {boolean}
 */
function isNavActive(path, pathname) {
  if (path === "/beta") {
    return pathname === "/beta" || pathname.startsWith("/beta/");
  }
  if (path === "/explore") {
    return pathname === "/explore" || pathname.startsWith("/explore/");
  }
  if (path.includes("/channels")) {
    return pathname === path || pathname.startsWith(`${path}/`);
  }
  return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * @param {Object} props
 * @param {{ group: string, items: { path: string, label: string, Icon: Function, badgeCount?: number, badgeAria?: string }[] }[]} props.sections
 * @param {string} props.pathname
 * @param {boolean} props.expanded
 * @param {() => void} [props.onNavigate]
 * @returns {JSX.Element}
 */
function NavSections({ sections, pathname, expanded, onNavigate }) {
  return (
    <>
      {sections.map((section) => (
        <div key={section.group} className="flex flex-col gap-0.5">
          <div
            className={cn(
              "flex h-8 shrink-0 items-end px-3 pb-1 font-mono text-[10px] font-medium uppercase tracking-wider text-text-muted",
              "overflow-hidden whitespace-nowrap transition-opacity duration-200",
              expanded ? "opacity-100" : "opacity-0"
            )}
            aria-hidden={!expanded}
          >
            {section.group}
          </div>
          {section.items.map(({ path, label, Icon, badgeCount = 0, badgeAria }) => {
            const active = isNavActive(path, pathname);
            const count = Number(badgeCount) || 0;
            const showBadge = count > 0;
            const badgeLabel = count > 99 ? "99+" : String(count);
            const ariaExtra = badgeAria || "unread";
            return (
              <Link
                key={`${section.group}-${path}`}
                to={path}
                title={showBadge ? `${label}, ${count} ${ariaExtra}` : label}
                aria-label={
                  showBadge ? `${label}, ${count} ${ariaExtra}` : label
                }
                aria-current={active ? "page" : undefined}
                onClick={onNavigate}
                className={cn(
                  "flex h-12 w-full items-center rounded-lg text-sm font-medium transition-colors",
                  expanded && "pr-3",
                  active
                    ? "bg-nav-active text-accent"
                    : "text-text-muted hover:bg-nav-hover hover:text-text-primary"
                )}
              >
                <span className="relative flex h-full w-14 shrink-0 items-center justify-center">
                  <Icon className={cn("shrink-0", active ? "text-accent" : "text-current")} />
                  {showBadge && !expanded ? (
                    <Badge
                      variant="default"
                      className="absolute right-1 top-1.5 min-w-4 justify-center px-1 py-0 text-[9px] leading-3.5"
                    >
                      {badgeLabel}
                    </Badge>
                  ) : null}
                </span>
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate whitespace-nowrap transition-opacity duration-200",
                    expanded ? "opacity-100" : "pointer-events-none opacity-0"
                  )}
                >
                  {label}
                </span>
                {showBadge && expanded ? (
                  <span
                    aria-hidden
                    className="inline-flex shrink-0 items-center justify-center rounded-full bg-tag-accent px-[7px] py-0.5 font-mono font-emphasis leading-none text-tag-accent-fg"
                    style={{ fontSize: "var(--font-size-label)" }}
                  >
                    {badgeLabel}
                  </span>
                ) : null}
              </Link>
            );
          })}
        </div>
      ))}
    </>
  );
}

/**
 * Blur focused control so the rail does not stay expanded after a click.
 * @returns {void}
 */
function blurActiveElement() {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
}

/**
 * Icon-rail sidebar: collapsed by default, expands on hover or keyboard focus (desktop).
 * Pass `variant="mobile"` for the hamburger overlay.
 *
 * @param {Object} props
 * @param {object|null} props.user
 * @param {"desktop"|"mobile"} [props.variant]
 * @param {boolean} [props.mobileOpen]
 * @param {() => void} [props.onMobileClose]
 * @returns {JSX.Element|null}
 */
function LeftSidebar({ user, variant = "desktop", mobileOpen = false, onMobileClose }) {
  const location = useLocation();
  const {
    pendingConnectionCount,
    pendingAffiliationCount,
    messagingUnreadCount,
    orgUnreadById,
  } = useUserRealtime();
  const sections = buildNavSections(user, {
    pendingConnectionCount,
    pendingAffiliationCount,
    messagingUnreadCount,
    orgUnreadById,
  });
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const [suppressHover, setSuppressHover] = useState(false);

  if (variant === "mobile") {
    if (!mobileOpen) return null;
    return (
      <div className="fixed inset-0 z-50 md:hidden">
        <button
          type="button"
          className="absolute inset-0 bg-ui-overlay"
          aria-label="Close navigation"
          onClick={onMobileClose}
        />
        <nav
          aria-label="Primary"
          className="absolute left-0 top-0 flex h-full w-60 flex-col gap-1 overflow-y-auto bg-ui-background px-2 pt-2 pb-12 shadow-shell-md"
        >
          <div className="mb-1 flex items-center justify-between px-2 py-1">
            <span className="font-display text-base font-medium text-text-primary">Menu</span>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-md text-text-muted hover:bg-nav-hover hover:text-text-primary"
              aria-label="Close navigation"
              onClick={onMobileClose}
            >
              <CloseIcon />
            </button>
          </div>
          <NavSections
            sections={sections}
            pathname={location.pathname}
            expanded
            onNavigate={onMobileClose}
          />
        </nav>
      </div>
    );
  }

  const expanded = (hovered || focusWithin) && !suppressHover;

  return (
    <nav
      aria-label="Primary"
      onMouseEnter={() => {
        if (!suppressHover) setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
        setSuppressHover(false);
      }}
      onFocus={() => setFocusWithin(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setFocusWithin(false);
          setSuppressHover(false);
        }
      }}
      className={cn(
        "fixed left-0 top-14 z-30 flex h-[calc(100dvh-3.5rem)] flex-col gap-1 overflow-x-hidden overflow-y-auto scrollbar-hover",
        "bg-secondary pt-2 pb-12",
        "transition-[width] duration-200 ease-out",
        expanded ? "w-60" : "w-14",
        "hidden md:flex"
      )}
    >
      <NavSections
        sections={sections}
        pathname={location.pathname}
        expanded={expanded}
        onNavigate={() => {
          setHovered(false);
          setFocusWithin(false);
          setSuppressHover(true);
          blurActiveElement();
        }}
      />
    </nav>
  );
}

/**
 * Hamburger control for opening the mobile nav.
 * @param {Object} props
 * @param {() => void} props.onClick
 * @returns {JSX.Element}
 */
export function MobileNavButton({ onClick }) {
  return (
    <button
      type="button"
      className="flex h-8 w-8 items-center justify-center rounded-md text-text-primary hover:bg-nav-hover md:hidden sm:h-9 sm:w-9"
      aria-label="Open navigation"
      onClick={onClick}
    >
      <MenuIcon />
    </button>
  );
}

export default LeftSidebar;
